import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaSpinner, FaArrowLeft, FaExternalLinkAlt, FaUser, FaTimes } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 600px;
  background: ${props => props.theme.colors.surface};
  border-radius: 10px;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 5px;
  display: flex;
  align-items: center;
  
  &:hover {
    background: rgba(255,255,255,0.1);
  }
`;

const ChatInfo = styled.div`
  flex: 1;
  
  h3 {
    margin: 0 0 0.25rem 0;
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.9;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: ${props => props.theme.colors.background};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.surfaceAlt};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: 3px;
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  max-width: 70%;
  align-self: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div`
  background: ${props => props.isOwn ? props.theme.colors.primary : props.theme.colors.surfaceAlt};
  color: ${props => props.isOwn ? 'white' : props.theme.colors.text};
  padding: 0.75rem 1rem;
  border-radius: 18px;
  border-bottom-left-radius: ${props => props.isOwn ? '18px' : '4px'};
  border-bottom-right-radius: ${props => props.isOwn ? '4px' : '18px'};
  word-wrap: break-word;
  line-height: 1.4;
`;

const MessageMeta = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MessageInput = styled.div`
  padding: 1rem;
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  background: ${props => props.theme.colors.surface};
`;

const InputContainer = styled.div`
  flex: 1;
  position: relative;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  resize: none;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.4;
  max-height: 100px;
  min-height: 40px;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SendButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    background: ${props => props.theme.colors.primaryDark};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: ${props => props.theme.colors.primary};
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${props => props.theme.colors.textSecondary};
  text-align: center;
  
  h4 {
    margin-bottom: 0.5rem;
    color: ${props => props.theme.colors.text};
  }
`;

const ProfileModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 2rem;
`;

const ProfileModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 10px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 4px 20px ${props => props.theme.colors.shadow};
`;

const ProfileModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  position: sticky;
  top: 0;
  background: ${props => props.theme.colors.surface};
  z-index: 1;
  
  h2 {
    color: ${props => props.theme.colors.text};
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    svg {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  transition: background 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
`;

const ProfileModalBody = styled.div`
  padding: 1.5rem;
`;

const ProfileSection = styled.div`
  margin-bottom: 1.5rem;
  
  h3 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 0.75rem;
    font-size: 1rem;
    font-weight: 600;
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.6;
    margin: 0;
  }
`;

const ProfileDetailGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ProfileDetailItem = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  
  strong {
    color: ${props => props.theme.colors.text};
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  span {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.95rem;
  }
`;

const SkillTag = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.35rem 0.75rem;
  border-radius: 15px;
  margin: 0.25rem 0.25rem 0.25rem 0;
  font-size: 0.85rem;
  font-weight: 500;
`;

const UserNameButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  font: inherit;
  text-decoration: underline;
  text-decoration-style: dotted;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Chat = ({ thread, onBack, onMessagesRead, onViewListing }) => {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const messagesEndRef = useRef(null);
  const textAreaRef = useRef(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (thread && user) {
      loadMessages();
      setupRealtimeSubscription();
      markMessagesAsRead();
    }

    return () => {
      if (subscription) {
        chatService.unsubscribe(subscription);
      }
    };
  }, [thread, user]);

  useEffect(() => {
    // After first load, set flag
    if (isInitialLoad.current && !loading && messages.length > 0) {
      isInitialLoad.current = false;
    }
  }, [messages, loading]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const { messages: threadMessages } = await chatService.getThreadWithMessages(thread.id);
      setMessages(threadMessages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const sub = chatService.subscribeToThread(thread.id, (payload) => {
      if (payload.eventType === 'INSERT' && payload.new) {
        // Add the new message to the list
        setMessages(prev => [...prev, {
          ...payload.new,
          profiles: { display_name: payload.new.sender_id === user.id ? profile.display_name : 'Other User' }
        }]);
        
        // Mark as read if it's not from the current user
        if (payload.new.sender_id !== user.id) {
          markMessagesAsRead();
        }
      }
    });
    
    setSubscription(sub);
  };

  const markMessagesAsRead = async () => {
    try {
      const result = await chatService.markMessagesAsRead(thread.id, user.id);
      
      // Notify parent if any messages were marked as read
      if (result && result.length > 0 && onMessagesRead) {
        onMessagesRead();
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    const messageText = newMessage.trim();
    if (!messageText || sending) return;

    setSending(true);
    
    try {
      const message = await chatService.sendMessage({
        threadId: thread.id,
        senderId: user.id,
        body: messageText
      });

      // Add message immediately to UI (optimistic update)
      setMessages(prev => [...prev, message]);
      
      // Clear input
      setNewMessage('');
      
      // Reset textarea height
      if (textAreaRef.current) {
        textAreaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = textAreaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 100)}px`;
    }
  };

  const getOtherParticipant = () => {
    if (!thread || !user) return null;
    
    if (thread.seeker_id === user.id) {
      return thread.entrepreneur_profile;
    } else {
      return thread.seeker_profile;
    }
  };

  const otherParticipant = getOtherParticipant();

  const handleViewListing = () => {
    if (onViewListing && thread?.listings) {
      onViewListing(thread.listings);
    }
  };

  const handleViewProfile = () => {
    setShowProfileModal(true);
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
  };

  return (
    <>
      <ChatContainer>
        <ChatHeader>
          <BackButton onClick={onBack}>
            <FaArrowLeft />
          </BackButton>
          <ChatInfo>
            <h3 onClick={handleViewListing} title="View listing details">
              {thread?.listings?.title || 'Chat'}
              {thread?.listings && <FaExternalLinkAlt size={14} />}
            </h3>
            <p>
              with <UserNameButton onClick={handleViewProfile} title="View profile">
                {otherParticipant?.display_name || 'Unknown User'}
              </UserNameButton>
            </p>
          </ChatInfo>
        </ChatHeader>

      <MessagesContainer>
        {loading ? (
          <LoadingContainer>
            <FaSpinner size={20} />
            <span style={{ marginLeft: '0.5rem' }}>Loading messages...</span>
          </LoadingContainer>
        ) : messages.length === 0 ? (
          <EmptyState>
            <h4>Start the conversation</h4>
            <p>Send the first message to get things started!</p>
          </EmptyState>
        ) : (
          <>
            {messages.map((message) => {
              const isOwn = message.sender_id === user.id;
              return (
                <Message key={message.id} isOwn={isOwn}>
                  <MessageBubble isOwn={isOwn}>
                    {message.body}
                  </MessageBubble>
                  <MessageMeta>
                    <span>{isOwn ? 'You' : message.profiles?.display_name || 'User'}</span>
                    <span>•</span>
                    <span>{formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}</span>
                  </MessageMeta>
                </Message>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </MessagesContainer>

      <MessageInput>
        <InputContainer>
          <TextArea
            ref={textAreaRef}
            placeholder="Type a message..."
            value={newMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={sending}
          />
        </InputContainer>
        <SendButton 
          onClick={handleSendMessage}
          disabled={sending || !newMessage.trim()}
        >
          {sending ? <FaSpinner /> : <FaPaperPlane />}
        </SendButton>
      </MessageInput>
    </ChatContainer>

    {showProfileModal && otherParticipant && (
      <ProfileModal onClick={handleCloseProfileModal}>
        <ProfileModalContent onClick={(e) => e.stopPropagation()}>
          <ProfileModalHeader>
            <h2>
              <FaUser />
              {otherParticipant.display_name || 'User Profile'}
            </h2>
            <CloseButton onClick={handleCloseProfileModal}>
              <FaTimes />
            </CloseButton>
          </ProfileModalHeader>
          <ProfileModalBody>
            {otherParticipant.bio && (
              <ProfileSection>
                <h3>About</h3>
                <p>{otherParticipant.bio}</p>
              </ProfileSection>
            )}

            {otherParticipant.skills && otherParticipant.skills.length > 0 && (
              <ProfileSection>
                <h3>Skills</h3>
                <div>
                  {otherParticipant.skills.map((skill, index) => (
                    <SkillTag key={index}>{skill}</SkillTag>
                  ))}
                </div>
              </ProfileSection>
            )}

            {otherParticipant.experience && otherParticipant.experience.length > 0 && (
              <ProfileSection>
                <h3>Experience</h3>
                {otherParticipant.experience.map((exp, index) => (
                  <div key={index} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: index < otherParticipant.experience.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                    <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{exp.title}</strong>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>{exp.company}</p>
                    {exp.location && <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', opacity: 0.8 }}>{exp.location}</p>}
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', opacity: 0.8 }}>
                      {exp.start_date} - {exp.current ? 'Present' : exp.end_date}
                    </p>
                    {exp.description && <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', lineHeight: 1.5 }}>{exp.description}</p>}
                  </div>
                ))}
              </ProfileSection>
            )}

            {otherParticipant.qualifications && otherParticipant.qualifications.length > 0 && (
              <ProfileSection>
                <h3>Qualifications</h3>
                {otherParticipant.qualifications.map((qual, index) => (
                  <div key={index} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: index < otherParticipant.qualifications.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                    <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{qual.degree}</strong>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>{qual.institution}</p>
                    {qual.field && <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', opacity: 0.8 }}>{qual.field}</p>}
                    {qual.graduation_year && <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', opacity: 0.8 }}>Graduated: {qual.graduation_year}</p>}
                    {qual.description && <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', lineHeight: 1.5 }}>{qual.description}</p>}
                  </div>
                ))}
              </ProfileSection>
            )}

            {otherParticipant.certifications && otherParticipant.certifications.length > 0 && (
              <ProfileSection>
                <h3>Certifications</h3>
                {otherParticipant.certifications.map((cert, index) => (
                  <div key={index} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: index < otherParticipant.certifications.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                    <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{cert.name}</strong>
                    <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>{cert.issuer}</p>
                    {cert.issue_date && <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', opacity: 0.8 }}>Issued: {cert.issue_date}</p>}
                    {cert.expiry_date && <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', opacity: 0.8 }}>Expires: {cert.expiry_date}</p>}
                    {cert.credential_url && (
                      <p style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                        <a 
                          href={cert.credential_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'underline' }}
                        >
                          View Credential
                        </a>
                      </p>
                    )}
                  </div>
                ))}
              </ProfileSection>
            )}

            {!otherParticipant.bio && 
             !otherParticipant.skills?.length && 
             !otherParticipant.experience?.length && 
             !otherParticipant.qualifications?.length && 
             !otherParticipant.certifications?.length && (
              <ProfileSection>
                <p style={{ textAlign: 'center', color: 'inherit' }}>
                  This user hasn't completed their profile yet.
                </p>
              </ProfileSection>
            )}
          </ProfileModalBody>
        </ProfileModalContent>
      </ProfileModal>
    )}
  </>
  );
};

export default Chat;
